import { Link, type Href } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import * as React from 'react';
import { Platform, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { androidStatusBarHeight } from '@/components/android-status-bar-band';
import { ProfileAffiliations } from '@/components/profiles/profile-affiliations';
import { ProfileScreen } from '@/components/profiles/profile-screen';
import { ProfileTopBar } from '@/components/profiles/profile-top-bar';
import { MemberQrControls } from '@/components/qr/member-qr-controls';
import { useTenant } from '@/components/tenant-provider';
import { BRAND } from '@sixam/config/brand';
import { Button } from '@sixam/ui/button';
import { Icon } from '@sixam/ui/icon';
import { Text } from '@sixam/ui/text';
import { ShieldAlertIcon } from 'lucide-react-native';
import type { TenantRole } from '@/lib/tenant';
import { fetchTenantMembers, type TenantMemberListItem } from '@/lib/tenant-members';
import type { TenantProfile } from '@/lib/tenant-profile';

function displayNameForTenant(tenant: TenantProfile | null) {
  return tenant?.name || 'Tenant profile';
}

function matchesMemberSearch(query: string, member: TenantMemberListItem) {
  if (!query) {
    return true;
  }

  return [member.fullName, member.role].some((value) => value?.toLowerCase().includes(query));
}

export default function AdminTenantProfileScreen() {
  const { activeTenant, membershipRole } = useTenant();
  const insets = useSafeAreaInsets();
  const [profileSearchQuery, setProfileSearchQuery] = React.useState('');
  const [isQrOpen, setIsQrOpen] = React.useState(false);
  const [isScannerOpen, setIsScannerOpen] = React.useState(false);
  // Restore the offset lost when the app root zeroed the top inset, so the
  // avatar and QR buttons clear the native search header. See the member profile
  // for the same fix.
  const headerTopOffset = insets.top + androidStatusBarHeight();

  return (
    <View className="flex-1 bg-background" collapsable={false}>
      <StatusBar style="light" />
      <View
        className="absolute left-0 right-0 top-0"
        pointerEvents="none"
        style={{ backgroundColor: BRAND.color.ink, height: headerTopOffset + 72 }}
      />
      {Platform.OS !== 'web' ? (
        <ProfileTopBar
          kind="tenant"
          onSearchChange={setProfileSearchQuery}
          searchPlaceholder="Search members"
        />
      ) : null}
      <AdminTenantProfilePanel
        key={activeTenant?.id ?? 'missing-tenant'}
        activeTenant={activeTenant}
        membershipRole={membershipRole}
        searchQuery={profileSearchQuery}
        topPadding={headerTopOffset + 52}
      />

      <MemberQrControls
        checkInTenantId={activeTenant?.id}
        onQrOpenChange={setIsQrOpen}
        onScannerOpenChange={setIsScannerOpen}
        qrOpen={isQrOpen}
        scannerOpen={isScannerOpen}
        tenantSlug={activeTenant?.slug}
        topOffset={headerTopOffset + 80}
      />
    </View>
  );
}

function AdminTenantProfilePanel({
  activeTenant,
  membershipRole,
  searchQuery,
  topPadding,
}: Readonly<{
  activeTenant: TenantProfile | null;
  membershipRole: TenantRole | null;
  searchQuery: string;
  topPadding: number;
}>) {
  const tenantSlug = activeTenant?.slug;
  const currentTenant = activeTenant;
  const displayName = displayNameForTenant(currentTenant);
  const initials = displayName.slice(0, 2).toUpperCase();
  const [members, setMembers] = React.useState<TenantMemberListItem[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = React.useState(false);
  const [membersError, setMembersError] = React.useState<string | null>(null);
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  React.useEffect(() => {
    let active = true;

    async function loadMembers(slug: string) {
      setIsLoadingMembers(true);
      try {
        const data = await fetchTenantMembers(slug);
        if (active) {
          setMembers(data);
          setMembersError(null);
        }
      } catch (loadError) {
        if (active) {
          setMembers([]);
          setMembersError(
            loadError instanceof Error ? loadError.message : 'Could not load tenant members.'
          );
        }
      } finally {
        if (active) {
          setIsLoadingMembers(false);
        }
      }
    }

    if (tenantSlug) {
      void loadMembers(tenantSlug);
    }

    return () => {
      active = false;
    };
  }, [tenantSlug]);

  const visibleMembers = members.filter((member) =>
    matchesMemberSearch(normalizedSearchQuery, member)
  );
  const memberAffiliations = visibleMembers.map((member) => {
    const name = member.fullName ?? '6am member';
    return {
      avatarUrl: member.avatarUrl,
      href: {
        pathname: '/(authenticated)/app/members/[id]',
        params: { id: member.id },
      } as Href,
      id: member.id,
      initials: name.slice(0, 2).toUpperCase(),
      name,
      subtitle: member.role,
    };
  });

  return (
    <ProfileScreen
      avatarAlt={`${displayName}'s avatar`}
      avatarUrl={currentTenant?.avatar_url}
      bio={
        currentTenant?.bio || 'Morning routes, sweaty logistics, and post-run coffee for the crew.'
      }
      contentInsetAdjustmentBehavior="never"
      displayName={displayName}
      initials={initials}
      stats={[
        { label: 'Locale', value: currentTenant?.locale ?? 'en' },
        { compact: true, label: 'Timezone', value: currentTenant?.timezone ?? 'Asia/Bangkok' },
        { compact: true, label: 'Role', value: membershipRole ?? 'admin' },
      ]}
      subtitle={currentTenant?.slug}
      topPadding={topPadding}
    >
      <Link href="/admin/moderation" asChild>
        <Button className="w-full" variant="destructive">
          <Icon as={ShieldAlertIcon} className="size-5 text-white" />
          <Text>Review safety reports</Text>
        </Button>
      </Link>
      <ProfileAffiliations
        description={
          isLoadingMembers
            ? 'Loading active tenant members.'
            : 'Active members attached to this tenant.'
        }
        emptyText={
          membersError ??
          (normalizedSearchQuery ? 'No members match this search.' : 'No active members yet.')
        }
        items={memberAffiliations}
        modalTitle="My members"
        title="My members"
      />
    </ProfileScreen>
  );
}
